import "../redesign.css";
import { useEffect } from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import { domainUrl } from "app/Api/axios";
import { useLocation } from "react-router-dom";

const AboutUs = () => {
  const location = useLocation();

  useEffect(() => { window.scrollTo(0, 0); }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "About Timetango | Best HRMS & Attendance Tracking Software in India",
    image: "https://timetango.in/assets/img/hero/Hero_Combine.png",
    description: "Discover Timetango, India's leading workforce attendance management system, offering advanced time tracking and productivity software for all businesses.",
    brand: {
      "@type": "Brand",
      name: "Time Tango",
      logo: "https://timetango.in/assets/logo3.svg",
      sameAs: [
        "https://www.facebook.com/people/TimeTango/61550584126267/",
        "https://www.linkedin.com/showcase/99884387/",
        "https://www.instagram.com/timetango.in/",
      ],
    },
    offers: {
      "@type": "Offer",
      url: "https://timetango.in",
      priceCurrency: "INR",
      price: "30",
    },
  };

  const canonicalURL = `${domainUrl}${location.pathname}`;

  return (
    <div className="tt-page">
      <Helmet>
        <title>About Timetango | Best HRMS &amp; Attendance Tracking Software in India</title>
        <meta name="description" content="Discover Timetango, India's leading workforce attendance management system, offering advanced time tracking and productivity software for all businesses." />
        <meta property="og:title" content="About Timetango | Best HRMS & Attendance Tracking Software in India" />
        <meta property="og:description" content="Discover Timetango, India's leading workforce attendance management system, offering advanced time tracking and productivity software for all businesses." />
        <meta property="og:image" content="https://timetango.in/assets/logo3.svg" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://timetango.in/" />
        <link rel="canonical" href="https://timetango.in/" />
        <meta property="og:site_name" content="Timetango" />
        <meta name="language" content="english" />
        <meta name="robots" content="index, follow" />
        <meta name="google-site-verification" content="HeqJ8arMIZIDpCmRH1zlrNu601VS7Wp92JoTy_9bARM" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <section style={{position:"relative",overflow:"hidden",background:"radial-gradient(120% 90% at 50% -10%, #fbf3ff 0%, #ffffff 55%)"}}>
        {/* Glow blobs */}
        <div style={{position:"absolute",top:40,left:-120,width:420,height:420,background:"radial-gradient(closest-side, rgba(152,149,244,0.28), rgba(152,149,244,0))",pointerEvents:"none"}} />
        <div style={{position:"absolute",top:100,right:-140,width:460,height:460,background:"radial-gradient(closest-side, rgba(224,89,240,0.22), rgba(224,89,240,0))",pointerEvents:"none"}} />
        <div style={{position:"relative",maxWidth:900,margin:"0 auto",padding:"80px 32px 60px",textAlign:"center",display:"flex",flexDirection:"column",alignItems:"center",gap:22}}>
          <span className="tt-eyebrow-pill">✦ About TimeTango</span>
          <h1 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:60,lineHeight:1.04,margin:0,color:"#140f1e",fontWeight:600,letterSpacing:"-0.02em",maxWidth:780}}>
            We give teams their <span className="tt-grad-text">mornings back</span>
          </h1>
          <p style={{margin:0,fontSize:20,lineHeight:1.6,color:"#5a5470",maxWidth:640}}>
            TimeTango started with one frustration: attendance shouldn't cost HR a single morning. So we built a platform that handles check-ins, activity and leave in the background — quietly, accurately, everywhere your people work.
          </p>
        </div>
      </section>

      {/* ── STATS BAND ── */}
      <div style={{borderTop:"1px solid #efecf6",borderBottom:"1px solid #efecf6",background:"#faf8fd"}}>
        <div className="tt-about-stats">
          <div className="tt-about-stat">
            <div className="tt-about-stat__num tt-grad-text">500+</div>
            <div className="tt-about-stat__label">Businesses onboard</div>
          </div>
          <div className="tt-about-stat tt-about-stat--bordered">
            <div className="tt-about-stat__num tt-grad-text">2M+</div>
            <div className="tt-about-stat__label">Check-ins logged</div>
          </div>
          <div className="tt-about-stat tt-about-stat--bordered">
            <div className="tt-about-stat__num tt-grad-text">4</div>
            <div className="tt-about-stat__label">Countries served</div>
          </div>
          <div className="tt-about-stat tt-about-stat--bordered">
            <div className="tt-about-stat__num tt-grad-text">₹30</div>
            <div className="tt-about-stat__label">Per user / month</div>
          </div>
        </div>
      </div>

      {/* ── OUR STORY ── */}
      <section style={{maxWidth:1140,margin:"0 auto",padding:"84px 32px 56px"}}>
        <div className="tt-about-story">
          <div>
            <span className="tt-eyebrow">Our story</span>
            <h2 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:38,margin:"14px 0 18px",color:"#140f1e",fontWeight:600,letterSpacing:"-0.01em"}}>
              Built from the gaps in every other system
            </h2>
            <p style={{margin:"0 0 16px",fontSize:16,lineHeight:1.7,color:"#5a5470"}}>
              We kept watching capable HR teams lose hours to paper sign-in sheets, chase down leave forms, and reconcile timesheets by hand. The tools meant to help were either too heavy, too rigid, or built for a headcount most companies never reach.
            </p>
            <p style={{margin:0,fontSize:16,lineHeight:1.7,color:"#5a5470"}}>
              TimeTango is our answer — an attendance and workforce platform that's accurate enough to trust, light enough to love, and priced so any growing team can start on day one. Today it runs quietly behind hundreds of businesses across India, the USA, the UK and the UAE.
            </p>
          </div>
          <div style={{position:"relative"}}>
            <div style={{position:"absolute",inset:"6% 8%",background:"linear-gradient(135deg,rgba(224,89,240,0.22),rgba(152,149,244,0.22))",filter:"blur(46px)",borderRadius:40,zIndex:0}} />
            <img src="/assets/img/hero/Hero_Combine.webp" alt="TimeTango dashboard" style={{position:"relative",zIndex:1,width:"100%",display:"block",filter:"drop-shadow(0 40px 60px rgba(70,30,110,0.2))"}} />
            <div className="tt-about-story-chip">
              <div style={{fontSize:11,color:"#8a839c"}}>Time HR gets back</div>
              <div style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:16,fontWeight:600,color:"#b81cd4"}}>~6 hrs / week</div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section style={{maxWidth:1100,margin:"0 auto",padding:"8px 32px 40px"}}>
        <div className="tt-about-mission">
          <div style={{position:"absolute",top:-120,right:-60,width:340,height:340,background:"radial-gradient(closest-side, rgba(224,89,240,0.16), rgba(224,89,240,0))",pointerEvents:"none"}} />
          <div style={{position:"relative",maxWidth:820}}>
            <span className="tt-eyebrow">Our mission</span>
            <p style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:30,lineHeight:1.4,margin:"16px 0 0",color:"#241d33",fontWeight:500,letterSpacing:"-0.01em"}}>
              To make attendance so effortless it disappears — so every business spends its time on the work that matters, not the admin around it.
            </p>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section style={{maxWidth:1200,margin:"0 auto",padding:"64px 32px 40px"}}>
        <div style={{textAlign:"center",maxWidth:600,margin:"0 auto 44px"}}>
          <span style={{fontSize:13,fontWeight:600,letterSpacing:"0.12em",textTransform:"uppercase",color:"#7d6cf0"}}>What we stand for</span>
          <h2 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:38,margin:"14px 0 0",color:"#140f1e",fontWeight:600}}>
            The values behind the product
          </h2>
        </div>
        <div className="tt-about-values">
          <div className="tt-card" style={{padding:30}}>
            <div className="tt-about-value-icon"><div style={{width:20,height:20,borderRadius:6,background:"linear-gradient(135deg,#e059f0,#9895f4)"}} /></div>
            <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:20,margin:"0 0 10px",color:"#140f1e",fontWeight:600}}>Useful over impressive</h3>
            <p style={{margin:0,fontSize:15,lineHeight:1.65,color:"#5a5470"}}>We ship features people actually reach for. No dashboards for the sake of dashboards — every screen has to earn its place in someone's day.</p>
          </div>
          <div className="tt-card" style={{padding:30}}>
            <div className="tt-about-value-icon" style={{background:"linear-gradient(135deg,rgba(152,149,244,0.16),rgba(224,89,240,0.12))"}}><div style={{width:20,height:20,borderRadius:"50%",border:"3px solid #9895f4"}} /></div>
            <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:20,margin:"0 0 10px",color:"#140f1e",fontWeight:600}}>Transparency, not surveillance</h3>
            <p style={{margin:0,fontSize:15,lineHeight:1.65,color:"#5a5470"}}>Activity insight should build trust between teams and managers — never erode it. We design for honesty on both sides of the screen.</p>
          </div>
          <div className="tt-card" style={{padding:30}}>
            <div className="tt-about-value-icon" style={{background:"linear-gradient(135deg,rgba(224,89,240,0.12),rgba(152,149,244,0.16))"}}><div style={{width:22,height:15,borderRadius:4,border:"3px solid #cd6ae0"}} /></div>
            <h3 style={{fontFamily:"'Space Grotesk',sans-serif",fontSize:20,margin:"0 0 10px",color:"#140f1e",fontWeight:600}}>Customer success is the metric</h3>
            <p style={{margin:0,fontSize:15,lineHeight:1.65,color:"#5a5470"}}>A sale isn't a finish line. We stay close, respond fast, and keep the product the most cost-effective way for your team to run attendance.</p>
          </div>
        </div>
      </section>

      {/* ── GLOBAL REACH ── */}
      <div style={{background:"#faf8fd",borderTop:"1px solid #efecf6",borderBottom:"1px solid #efecf6"}}>
        <div style={{maxWidth:1120,margin:"0 auto",padding:"56px 32px",display:"flex",alignItems:"center",gap:26,flexWrap:"wrap",justifyContent:"center"}}>
          <span style={{fontSize:15,color:"#5a5470",fontWeight:500}}>Trusted by teams in</span>
          <div style={{display:"flex",gap:34,flexWrap:"wrap",justifyContent:"center"}}>
            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:19,color:"#241d33"}}>🇮🇳 India</span>
            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:19,color:"#241d33"}}>🇺🇸 USA</span>
            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:19,color:"#241d33"}}>🇬🇧 UK</span>
            <span style={{fontFamily:"'Space Grotesk',sans-serif",fontWeight:700,fontSize:19,color:"#241d33"}}>🇦🇪 UAE</span>
          </div>
          <span style={{fontSize:14,color:"#9a93ac"}}>· across corporate, healthcare, manufacturing, retail &amp; logistics</span>
        </div>
      </div>

      {/* ── FINAL CTA ── */}
      <section style={{maxWidth:1100,margin:"64px auto 0",padding:"0 32px"}}>
        <div className="tt-cta-band" style={{borderRadius:28,padding:"60px 40px"}}>
          <h2 style={{fontSize:42,letterSpacing:"-0.01em"}}>Come build a calmer workday with us</h2>
          <p style={{maxWidth:560}}>Join the hundreds of businesses who've made HR paperless, secure and pleasantly fast — starting from ₹30 per user.</p>
          <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
            <Link to="/company-register" className="tt-btn tt-btn--lg" style={{background:"#fff",color:"#a411c1",borderRadius:12,fontWeight:600}}>Register your company →</Link>
            <a href="https://calendly.com/amol-amlapure" target="_blank" rel="noreferrer" className="tt-btn tt-btn--lg" style={{background:"rgba(255,255,255,0.16)",color:"#fff",border:"1px solid rgba(255,255,255,0.45)",borderRadius:12}}>Schedule a demo</a>
          </div>
        </div>
      </section>

      {/* Bottom spacer before footer */}
      <div style={{height:72}} />
    </div>
  );
};

export default AboutUs;
